import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Save, UserCog, Check } from "lucide-react";
import api, { formatApiError } from "../lib/api";

export default function UserForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [stores, setStores] = useState([]);
  const [form, setForm] = useState({
    username: "",
    name: "",
    email: "",
    password: "",
    group: "store",
    store_id: "",
    store_ids: [],
  });

  useEffect(() => {
    api.get("/stores").then((r) => setStores(r.data || []));
    if (!isEdit) return;
    api
      .get(`/users`)
      .then((r) => {
        const u = r.data.find((x) => x.id === id);
        if (u) {
          setForm({
            ...u,
            password: "",
            store_id: u.store_id || "",
            store_ids: Array.isArray(u.store_ids) ? u.store_ids : [],
          });
        }
      })
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  const upd = (k) => (e) => {
    const v = e?.target?.value ?? e;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const toggleStore = (storeId) => {
    setForm((f) => {
      const has = f.store_ids.includes(storeId);
      return {
        ...f,
        store_ids: has ? f.store_ids.filter((x) => x !== storeId) : [...f.store_ids, storeId],
      };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.group === "admin") {
        payload.store_id = null;
        payload.store_ids = [];
      } else if (payload.group === "store") {
        payload.store_ids = [];
        if (!payload.store_id) payload.store_id = null;
      } else if (payload.group === "superstore") {
        payload.store_id = null;
      }
      if (isEdit && !payload.password) delete payload.password;
      if (isEdit) {
        await api.patch(`/users/${id}`, payload);
        toast.success(t("userForm.updated"));
      } else {
        await api.post("/users", payload);
        toast.success(t("userForm.created"));
      }
      navigate("/users");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const sortedStores = useMemo(
    () => [...stores].sort((a, b) => a.name.localeCompare(b.name)),
    [stores]
  );

  if (loading) return <div className="pg-card p-10 text-center text-slate-400">{t("common.loading")}</div>;

  const mode = isEdit ? t("userForm.modeEdit") : t("userForm.modeCreate");

  return (
    <div data-testid="user-form-page">
      <button onClick={() => navigate(-1)} className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-2 mb-4">
        <ArrowLeft size={15} /> {t("common.back")}
      </button>
      <div className="mb-7">
        <div className="text-xs uppercase tracking-[0.32em] text-slate-400 mb-1">
          {t("userForm.breadcrumb", { mode })}
        </div>
        <h1 className="text-3xl font-extrabold text-slate-900">
          {isEdit ? `${t("userForm.modeEdit")} ${form.name || form.username}` : t("userForm.addNew")}
        </h1>
      </div>

      <form onSubmit={submit} className="space-y-6 max-w-3xl">
        <section className="pg-card p-6 lg:p-8">
          <div className="field-section-title mb-5">
            <UserCog size={16} /> {t("userForm.details")}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label={t("userForm.name")}>
              <input className={inp} required value={form.name} onChange={upd("name")} data-testid="u-name" />
            </Field>
            <Field label={t("userForm.username")}>
              <input className={inp} required value={form.username} onChange={upd("username")} disabled={isEdit} data-testid="u-username" />
            </Field>
            <Field label={t("userForm.email")}>
              <input className={inp} type="email" value={form.email || ""} onChange={upd("email")} />
            </Field>
            <Field label={isEdit ? t("userForm.passwordOptional") : t("userForm.passwordRequired")}>
              <input className={inp} type="password" required={!isEdit} value={form.password || ""} onChange={upd("password")} data-testid="u-password" />
            </Field>
            <Field label={t("userForm.group")}>
              <select className={inp} value={form.group} onChange={upd("group")} data-testid="u-group">
                <option value="admin">{t("groups.admin")}</option>
                <option value="store">{t("groups.store")}</option>
                <option value="superstore">{t("groups.superstore")}</option>
              </select>
            </Field>
            {form.group === "store" && (
              <Field label={t("userForm.assignedStore")}>
                <select className={inp} value={form.store_id || ""} onChange={upd("store_id")} data-testid="u-store">
                  <option value="">{t("common.none")}</option>
                  {sortedStores.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </Field>
            )}
          </div>

          {form.group === "superstore" && (
            <div className="mt-6">
              <div className="field-label flex items-center justify-between">
                <span>{t("userForm.assignedStores")}</span>
                <span className="text-xs text-slate-400 normal-case tracking-normal font-normal">
                  {form.store_ids.length} / {sortedStores.length}
                </span>
              </div>
              <p className="text-xs text-slate-500 mb-3">{t("userForm.selectStores")}</p>
              {sortedStores.length === 0 ? (
                <div className="text-sm text-slate-400 border border-dashed border-slate-200 rounded-lg p-6 text-center">
                  {t("userForm.noStoresHint")}
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-72 overflow-y-auto pr-1" data-testid="u-store-multi">
                  {sortedStores.map((s) => {
                    const checked = form.store_ids.includes(s.id);
                    return (
                      <label
                        key={s.id}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border cursor-pointer transition select-none ${
                          checked
                            ? "border-cyan-400 bg-cyan-50/60 ring-1 ring-cyan-100"
                            : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"
                        }`}
                        data-testid={`u-store-opt-${s.id}`}
                      >
                        <span
                          className={`w-5 h-5 rounded-md border grid place-items-center flex-shrink-0 transition ${
                            checked
                              ? "bg-gradient-to-br from-cyan-500 to-blue-600 border-cyan-500 text-white"
                              : "border-slate-300 bg-white"
                          }`}
                        >
                          {checked && <Check size={14} />}
                        </span>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleStore(s.id)}
                          className="sr-only"
                        />
                        <span className="flex-1 min-w-0">
                          <span className="block text-sm font-medium text-slate-800 truncate">{s.name}</span>
                          <span className="block text-[11px] text-slate-400 mono truncate">MID · {s.machine_id}</span>
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </section>

        <div className="flex justify-end gap-3">
          <button type="button" onClick={() => navigate(-1)} className="px-5 py-2.5 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 font-semibold text-sm">
            {t("common.cancel")}
          </button>
          <button type="submit" disabled={saving} className="px-5 py-2.5 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-semibold text-sm flex items-center gap-2 disabled:opacity-60" data-testid="save-user-btn">
            <Save size={15} />
            {saving ? t("userForm.saving") : isEdit ? t("userForm.updateUser") : t("userForm.createUser")}
          </button>
        </div>
      </form>
    </div>
  );
}

const inp = "w-full px-3.5 py-2.5 rounded-lg border border-slate-200 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100 outline-none transition bg-white text-sm disabled:bg-slate-50 disabled:text-slate-500";
function Field({ label, children }) {
  return (
    <div>
      <label className="field-label">{label}</label>
      {children}
    </div>
  );
}
